import React, { useCallback, useRef, useState } from 'react';
import classnames from 'classnames';
import { ColdEmailPlanName } from '../../cold-email-plan-card/types';
import Button from '../../../../../../../../../shared/design-system/components/atoms/button';
import { IProps1 } from '../types';
import {
  isAnnuallyPlan,
  isQuarterlyPlan,
} from '../../../../outreach-plans/utils/helper';
import { BillingOption } from '../../../../../../../../../shared/utils/billing-options';
import { SelectPlanCTAText } from '../../../../../../../../../shared/design-system/components/molecules/pricing-card/types';
import {
  getButtonLabel,
  getFestiveDiscountPercentage,
  getIsFestiveDiscountActive,
  getPlanDetailsObj,
} from '../../../../outreach-plans/components/outreach-plan-card/utils/helper';
import { isUserInPaidPlan } from '../../../../../../../../../shared/design-system/components/molecules/subscription-plan-details/helper/helper';
import { openChatSupport } from '../../../../../../../../../shared/utils/open-chat-support';
import { getIsDemoAccount } from '../../../../../../../../../shared/utils/user-details';
import { Plan } from '../../../../../types';
import UpdatePlanModal from '../../../../outreach-plans/components/update-plan-modal/update-plan-modal';
import GlobalRestrictionModal from '../../../../../../../../home/components/global-restriction-modal';

const FeaturesHeader: React.FC<IProps1> = ({
  planDetailObject,
  sendGetPlansResponse,
  handlePlanSelect,
  planCycle,
  sendGetAccountSubscriptionResponse,
  isPurchaseInProgress,
  onSchedule,
  isScheduleRequestLoading,
}) => {
  const [showGlobalRestrictionModal, setShowGlobalRestrictionModal] = useState(
    false,
  );
  const [showUpdatePlanModal, setShowUpdatePlanModal] = useState(false);

  const planDetailRef = useRef<Plan>(null);

  const isUserSubscribed = isUserInPaidPlan(
    sendGetAccountSubscriptionResponse?.planCode,
  );

  const isFestiveDiscountActive = getIsFestiveDiscountActive();
  const festiveDiscountPercentage = getFestiveDiscountPercentage();

  const getAdjustedAmount = useCallback(
    (yearly: any, quarterly: any, monthly: any): number | null => {
      if (planCycle === BillingOption.ANNUALLY && yearly?.amount) {
        return Number(yearly?.amount) / 12;
      }
      if (planCycle === BillingOption.QUARTERLY && quarterly?.amount) {
        return Number(quarterly?.amount) / 3;
      }
      return Number(monthly?.amount || 0);
    },
    [planCycle],
  );

  const getColdEmailPlans = useCallback(
    (
      planName: string,
      adjustedPlanDetailPlus: any,
      adjustedPlanDetail: any,
    ) => {
      if (planName === ColdEmailPlanName.Custom) {
        return 'Price';
      }
      if (isFestiveDiscountActive && isAnnuallyPlan(planCycle)) {
        const planDetail = planDetailObject?.find(
          (plans) => plans?.planName?.includes(planName) && plans?.id >= 480,
        );
        return `$${
          Number(
            Number(planDetail?.amount) * (1 - festiveDiscountPercentage / 100),
          )?.toFixed(0) ?? 0
        }`;
      }
      if (planName === ColdEmailPlanName.ScalePlus) {
        return `$${Number(adjustedPlanDetailPlus)?.toFixed(0) ?? 0}`;
      }
      return `$${Number(adjustedPlanDetail)?.toFixed(0) ?? 0}`;
    },
    [
      planCycle,
      planDetailObject,
      isFestiveDiscountActive,
      festiveDiscountPercentage,
    ],
  );

  const getMinCountsByPlanId = (planId?: number) => {
    const basePlan = planDetailObject?.find((p) => p?.id === planId);
    const prospectsMin =
      basePlan?.dynamicCounts?.find((dc) => dc?.label === 'Prospects')?.count
        ?.min ?? 0;
    const emailsMin =
      basePlan?.dynamicCounts?.find((dc) => dc?.label === 'Emails')?.count
        ?.min ?? 0;
    return { prospectsCount: prospectsMin, emailsCount: emailsMin };
  };

  const handleDynamicCustomPlan = (id, customPlanName) => {
    const minCounts = getMinCountsByPlanId(id);
    handlePlanSelect(id, customPlanName, minCounts);
  };

  const handleSubmitBtn = ({
    btnLabel,
    planName,
    planDetail,
    quarterlyPlan,
    yearlyPlan,
    quarterlyPlus,
    yearlyPlus,
  }) => {
    if (getIsDemoAccount()) {
      setShowGlobalRestrictionModal(true);
      return;
    }

    if (btnLabel === SelectPlanCTAText.UpdatePlan) {
      planDetailRef.current = planDetail;
      setShowUpdatePlanModal(true);
      return;
    }

    if (planName === ColdEmailPlanName.Custom) {
      openChatSupport();
      return;
    }
    if (isQuarterlyPlan(planCycle)) {
      // Use Plus plans for Scale Plus, otherwise use regular plans
      const selectedPlan =
        planName === ColdEmailPlanName.ScalePlus
          ? quarterlyPlus
          : quarterlyPlan;
      handleDynamicCustomPlan(selectedPlan?.id, selectedPlan?.planName);
      return;
    }
    if (isAnnuallyPlan(planCycle)) {
      // Use Plus plans for Scale Plus, otherwise use regular plans
      const selectedPlan =
        planName === ColdEmailPlanName.ScalePlus ? yearlyPlus : yearlyPlan;
      handleDynamicCustomPlan(selectedPlan?.id, selectedPlan?.planName);
      return;
    }
    handleDynamicCustomPlan(planDetail?.id, planDetail?.planName);
  };

  return (
    <>
      <div className="features-header d-flex flex-row text-left">
        <div className="font-14 font-semibold blue-txt-15 feature-title-container">
          <div className="feature-title">
            <span>FEATURE</span>
          </div>
        </div>

        {Object?.values(ColdEmailPlanName)?.map((planName) => {
          const planDetail = planDetailObject?.find(
            (plans) => plans?.planName?.includes(planName) && plans?.id >= 480,
          );

          const yearlyPlan = planDetailObject?.find(
            (plans) =>
              plans?.planName?.includes('(Yearly)') &&
              plans?.planName?.includes(planName) &&
              plans.id >= 480 &&
              plans.id !== 491,
          );

          const yearlyPlus = planDetailObject?.find(
            (plans) =>
              plans?.planName?.includes('Plus (Yearly)') &&
              plans?.planName?.includes(planName),
          );

          const quarterlyPlan = planDetailObject?.find(
            (plans) =>
              plans?.planName?.includes('(Quarterly)') &&
              plans?.planName?.includes(planName),
          );

          const quarterlyPlus = planDetailObject?.find(
            (plans) =>
              plans?.planName?.includes('Plus (Quarterly)') &&
              plans?.planName?.includes(planName),
          );

          const monthlyPlus = planDetailObject?.find(
            (plans) =>
              plans?.planName?.includes('Plus (Monthly)') &&
              plans?.planName?.includes(planName),
          );

          const planDetails = getPlanDetailsObj(
            yearlyPlan ?? yearlyPlus,
            isUserSubscribed,
          );

          const btnLabel = getButtonLabel({
            plan: planDetails,
            sendGetAccountSubscriptionResponse,
            sendGetPlansResponse,
            planCycle,
          });

          const getButtonTheme = () =>
            planName === ColdEmailPlanName.Professional
              ? Button.Theme.Solid
              : Button.Theme.Ghost;

          const getButtonLabelTxt = () => {
            if (planName === ColdEmailPlanName.Custom) {
              return 'Custom';
            }
            return btnLabel;
          };
          const btnClassName = classnames([
            {
              'ghost-primary-new': planDetails,
            },
            {
              'ghost-primary-new-disable':
                btnLabel === SelectPlanCTAText.DowngradePlan,
            },
            {
              'solid-primary-new':
                btnLabel !== SelectPlanCTAText.CurrentPlan &&
                btnLabel !== SelectPlanCTAText.DowngradePlan &&
                planDetails?.isBestValuePlan,
            },
            {
              'ghost-primary-new custom-plan-btn':
                planName === ColdEmailPlanName.Custom,
            },
            {
              'ghost-secondary-new-disable':
                btnLabel === SelectPlanCTAText.CurrentPlan,
            },
          ]);

          const adjustedPlanDetail = getAdjustedAmount(
            yearlyPlan,
            quarterlyPlan,
            planDetail,
          );
          const adjustedPlanDetailPlus = getAdjustedAmount(
            yearlyPlus,
            quarterlyPlus,
            monthlyPlus,
          );

          return (
            <div
              key={planName}
              className={`font-14 font-bold blue-txt-15 plan-name ${planName?.toLowerCase()}-plan-name flex-column justify-content-between feature-header-plans ${
                planName === ColdEmailPlanName.Professional
                  ? 'professional-header'
                  : ''
              }`}
            >
              <span className="feature-planName">{planName || ''}</span>

              <div className="comparison-plan-header">
                <span className="feature-header-price">
                  {getColdEmailPlans(
                    planName,
                    adjustedPlanDetailPlus,
                    adjustedPlanDetail,
                  )}
                </span>
                {(isAnnuallyPlan(planCycle) || isQuarterlyPlan(planCycle)) && (
                  <span className="feature-header-annual-price">
                    {planName === ColdEmailPlanName.ScalePlus
                      ? `$${Number(monthlyPlus?.amount || '0')?.toFixed(0)}`
                      : planName !== ColdEmailPlanName.Custom &&
                        `$${Number(planDetail?.amount || '0')?.toFixed(0)}`}
                  </span>
                )}
                <span className="month-text">/Month</span>
              </div>
              <Button
                variant={Button.Variant.Primary}
                theme={getButtonTheme()}
                onClick={() => {
                  handleSubmitBtn({
                    btnLabel,
                    planName,
                    planDetail,
                    quarterlyPlan,
                    yearlyPlan,
                    quarterlyPlus,
                    yearlyPlus,
                  });
                }}
                className={`w-100 ${btnClassName} upgrade-btn-billing ${
                  btnLabel === SelectPlanCTAText.DowngradePlan
                    ? 'solid-primary-news'
                    : ''
                }`}
                isLoading={isPurchaseInProgress}
                disabled={
                  !['Contact Us', 'Custom']?.includes(getButtonLabelTxt()) &&
                  (btnLabel === SelectPlanCTAText.CurrentPlan ||
                    btnLabel === SelectPlanCTAText.DowngradePlan ||
                    isPurchaseInProgress)
                }
              >
                {getButtonLabelTxt()}
              </Button>
            </div>
          );
        })}
      </div>
      <GlobalRestrictionModal
        show={showGlobalRestrictionModal}
        onClose={() => setShowGlobalRestrictionModal(false)}
        title="You’re about to leave the demo account"
        content="Upgrading a plan requires a real Saleshandy account. You’ll be redirected to log in or sign up and continue from your actual account. Do you want to proceed?"
        redirectTo="billing/upgrade-plan/email-outreach"
      />
      <UpdatePlanModal
        show={showUpdatePlanModal}
        onCancel={() => {
          setShowUpdatePlanModal(false);
          planDetailRef.current = null;
        }}
        onPayNow={() => {
          handleDynamicCustomPlan(
            planDetailRef?.current?.id,
            planDetailRef?.current?.planName,
          );
        }}
        onSchedule={onSchedule}
        subscriptionStatus={sendGetAccountSubscriptionResponse.status}
        isScheduleRequestLoading={isScheduleRequestLoading}
      />
    </>
  );
};

export default FeaturesHeader;
